import React from 'react';
import { Box, Tab, Tabs } from '@mui/material';
import {
  Public as GlobalIcon,
  Store as StoreIcon,
  Business as BranchIcon,
} from '@mui/icons-material';
import {
  useDashboardLocation,
  type DashboardLocation,
} from '../../contexts/DashboardLocationContext';

function getLocationIcon(loc: DashboardLocation) {
  return loc.type === 'WAREHOUSE' ? (
    <StoreIcon fontSize="small" />
  ) : (
    <BranchIcon fontSize="small" />
  );
}

type DashboardMode = 'light' | 'dark';

export const DashboardScopeTabs: React.FC<{
  mode?: DashboardMode;
  /** Opcional: se llama cuando cambia la ubicación (para sincronizar con filtros externos) */
  onScopeChange?: (scope: 'global' | number) => void;
}> = ({ mode = 'dark', onScopeChange }) => {
  const { scope, setScope, warehouseLocations, branchLocations, locationsLoading } =
    useDashboardLocation();
  const isLight = mode === 'light';

  if (locationsLoading) {
    return (
      <Box sx={{ mb: 2, minHeight: 48, display: 'flex', alignItems: 'center' }}>
        <Tabs value={false} sx={{ minHeight: 0 }}>
          <Tab label="Cargando..." disabled />
        </Tabs>
      </Box>
    );
  }

  const allTabs: { value: 'global' | number; label: string; icon?: React.ReactNode }[] = [
    { value: 'global', label: 'Global', icon: <GlobalIcon fontSize="small" /> },
    ...warehouseLocations.map((l) => ({
      value: l.id as number,
      label: l.name,
      icon: getLocationIcon(l),
    })),
    ...branchLocations.map((l) => ({
      value: l.id as number,
      label: l.name,
      icon: getLocationIcon(l),
    })),
  ];

  const currentValue = scope === 'global' ? 'global' : scope;

  return (
    <Box
      sx={(theme) => ({
        mb: 3,
        p: 1.5,
        borderRadius: 2,
        ...(isLight
          ? {
              background: '#ffffff',
              border: '1px solid #bae6fd',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
            }
          : {
              background: 'rgba(255, 255, 255, 0.04)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }),
      })}
    >
      <Tabs
        value={allTabs.some((t) => t.value === currentValue) ? currentValue : 'global'}
        onChange={(_, v) => {
          const newScope = v === 'global' ? 'global' : (v as number);
          setScope(newScope);
          onScopeChange?.(newScope);
        }}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        TabIndicatorProps={{ sx: { display: 'none' } }}
        sx={(theme) => ({
          minHeight: 0,
          '& .MuiTabs-flexContainer': { gap: 0.5 },
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 500,
            minHeight: 44,
            py: 1,
            px: 2,
            borderRadius: 2,
            ...(isLight
              ? {
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    color: theme.palette.text.primary,
                    backgroundColor: '#e0f2fe',
                  },
                  '&.Mui-selected': {
                    color: '#06b6d4',
                    fontWeight: 600,
                    backgroundColor: 'rgba(6, 182, 212, 0.12)',
                    border: '1px solid rgba(6, 182, 212, 0.35)',
                    '&:hover': { backgroundColor: 'rgba(6, 182, 212, 0.18)' },
                  },
                }
              : {
                  color: 'rgba(255, 255, 255, 0.6)',
                  '&:hover': {
                    color: 'rgba(255, 255, 255, 0.9)',
                    backgroundColor: 'rgba(255, 255, 255, 0.06)',
                  },
                  '&.Mui-selected': {
                    color: '#ffffff',
                    fontWeight: 600,
                    backgroundColor: 'rgba(20, 184, 166, 0.25)',
                    border: '1px solid rgba(20, 184, 166, 0.5)',
                    '&:hover': { backgroundColor: 'rgba(20, 184, 166, 0.3)' },
                  },
                }),
            transition: 'all 0.2s ease',
            '& .MuiTab-iconWrapper': { color: 'inherit' },
          },
        })}
      >
        {allTabs.map((tab) => (
          <Tab
            key={tab.value}
            value={tab.value}
            label={
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                {tab.icon}
                <span>{tab.label}</span>
              </Box>
            }
          />
        ))}
      </Tabs>
    </Box>
  );
};
